/**
 * The Hand helper's "Suggestions" card under "Your hand" in the round form (Indra, 2026-10-04): which patterns the tiles entered so far are
 * closest to, fewest tiles needed first, with limit hands on their own "Want to Win Big?" line. It only appears once 8 tiles are entered
 * and the House Rules switch is on (never in a leaderboard game — see `handHelperOn`), refreshes whenever the tiles change, and is always
 * labelled as the app's suggestion, the decision being the player's. The numbers come from the ruleset (`Ruleset.suggest`); this file only draws them.
 */

import { getRuleset } from '../rules';
import type { Tile } from '../rules/tiles';
import type { HeldKong, Suggestion, Suggestions } from '../rules/types';
import type { Game } from '../game/types';
import { esc } from './helpers';

/** Mirrors the engine's own minimum, so the screen can say "once you've entered 8 tiles" without importing a ruleset. */
export const SUGGESTIONS_FROM_TILES = 8;

function row(x: Suggestion): string {
  return `<div class="sg-row">
    <div><strong>${esc(x.label)}</strong> <span class="tag">${x.fan} fan</span></div>
    <div class="sg-sub">${x.need} tile${x.need === 1 ? '' : 's'} needed · ${esc(x.note)}</div>
    ${
      x.keep.length > 0
        ? `<span class="sg-keep" aria-label="Tiles you already hold for this">${x.keep.map((t) => `<img src="/tile-icons/${esc(t)}.png" alt="${esc(t)}" />`).join('')}</span>`
        : ''
    }
  </div>`;
}

export function suggestionsCardHtml(entered: number, s: Suggestions | undefined): string {
  if (entered < SUGGESTIONS_FROM_TILES) {
    return `<div class="card sg-card"><div class="sg-head"><strong>Suggestions</strong></div>
      <p class="muted sg-plain">Suggestions appear once you've entered ${SUGGESTIONS_FROM_TILES} tiles (${entered} so far).</p></div>`;
  }
  if (!s) return ''; // the hand is complete: the preview takes over from here
  const nothing = s.regular.length === 0 && s.big.length === 0;
  return `<div class="card sg-card">
    <div class="sg-head"><strong>Suggestions</strong> <span class="muted">— the app may suggest, the decision is yours</span></div>
    <div class="sg-basis">Based on the ${s.entered} tile${s.entered === 1 ? '' : 's'} you entered${s.entered < 13 ? ' — enter all 13 for a better answer' : ''}. Each pattern's own fan only.</div>
    ${s.regular.map(row).join('')}
    ${nothing ? '<p class="muted sg-plain">Nothing is close yet.</p>' : ''}
    ${s.big.length > 0 ? `<div class="sg-big">Want to Win Big?</div>${s.big.map(row).join('')}` : ''}
    <p class="muted sg-foot">Self-draw, fully concealed, flowers and wind bonuses are added when you record the round. It can't see what other players have discarded.</p>
  </div>`;
}

export interface HelperSource {
  read(): { concealed: Tile[]; kongs: HeldKong[] };
}

/** Keeps the card in `container` up to date. `refresh()` is called whenever the tiles change. */
export function mountHandHelper(container: HTMLElement, game: Pick<Game, 'rulesetId' | 'settings'>, source: HelperSource): { refresh(): void } {
  const refresh = (): void => {
    const { concealed, kongs } = source.read();
    const entered = concealed.length + kongs.length * 4;
    let suggestions: Suggestions | undefined;
    try {
      suggestions = getRuleset(game.rulesetId).suggest?.(concealed, kongs, game.settings);
    } catch {
      container.innerHTML = ''; // advice must never get in the way of recording a round
      return;
    }
    container.innerHTML = suggestionsCardHtml(entered, suggestions);
  };
  refresh();
  return { refresh };
}
